import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  activateCampaign,
  boostPost,
  createAd,
  createPausedCampaign,
  getCampaign,
  listCampaignLeads,
  createMarketingConnection,
  deleteMarketingConnection,
  listCampaigns,
  listMarketingPosts,
  pauseCampaign,
  searchInterests,
  refreshCampaignInsights,
  listMarketingConnections,
  syncCampaigns,
  testMarketingConnection,
  updateMarketingConnection,
  uploadAdImage,
} from "api/marketing";
import { apiErrorMessage } from "./marketingFormat";

const CAMPAIGN_PAGE_SIZE = 20;
const POST_PAGE_SIZE = 12;

export const marketingKeys = {
  connection: ["marketing", "connection"],
  campaigns: ["marketing", "campaigns"],
  campaign: (id) => ["marketing", "campaign", id],
  posts: (connectionId, source) => ["marketing", "posts", connectionId, source],
};

const unwrap = (res) => res?.data?.data;

export function useMarketingConnections() {
  return useQuery(
    marketingKeys.connection,
    () => listMarketingConnections().then((res) => unwrap(res)?.connections || []),
    { refetchOnMount: true }
  );
}

export function useCampaigns(connectionId) {
  return useInfiniteQuery(
    [...marketingKeys.campaigns, connectionId ?? "all"],
    ({ pageParam = 0 }) =>
      listCampaigns({ connectionId, page: pageParam, size: CAMPAIGN_PAGE_SIZE }).then(unwrap),
    {
      refetchOnMount: true,
      getNextPageParam: (last) =>
        last && last.currentPage + 1 < last.totalPages ? last.currentPage + 1 : undefined,
    }
  );
}

export function useCampaignDetail(id) {
  return useQuery(marketingKeys.campaign(id), () => getCampaign(id).then(unwrap), {
    enabled: id != null,
    refetchOnMount: true,
  });
}

export function useCampaignLeads(id, enabled) {
  return useQuery(["marketing", "leads", id], () => listCampaignLeads(id).then(unwrap), {
    enabled: enabled && id != null,
    refetchOnMount: true,
  });
}

export function usePosts(connectionId, source) {
  return useInfiniteQuery(
    marketingKeys.posts(connectionId, source),
    ({ pageParam }) =>
      listMarketingPosts({ connectionId, source, size: POST_PAGE_SIZE, cursor: pageParam }).then(
        unwrap
      ),
    {
      enabled: connectionId != null,
      refetchOnMount: true,
      getNextPageParam: (last) => last?.nextCursor || undefined,
    }
  );
}

export function useInterestSearch(connectionId, text) {
  const query = text.trim();
  return useQuery(
    ["marketing", "interests", connectionId, query],
    () => searchInterests({ connectionId, q: query }).then((res) => unwrap(res)?.interests || []),
    { enabled: connectionId != null && query.length >= 2, staleTime: 5 * 60 * 1000 }
  );
}

function useAction(action, { success, failure, invalidate }) {
  const queryClient = useQueryClient();
  return useMutation(action, {
    onSuccess: (res, variables) => {
      const message = typeof success === "function" ? success(unwrap(res), variables) : success;
      if (message) toast.success(message);
      invalidate.forEach((key) => queryClient.invalidateQueries(key));
    },
    onError: (error) => toast.error(apiErrorMessage(error, failure)),
  });
}

const CAMPAIGN_VIEWS = [marketingKeys.campaigns, ["marketing", "campaign"]];

export function useMarketingActions() {
  return {
    createConnection: useAction(createMarketingConnection, {
      success: "Meta account added",
      failure: "Could not add the Meta account",
      invalidate: [marketingKeys.connection],
    }),
    updateConnection: useAction(({ id, body }) => updateMarketingConnection(id, body), {
      success: "Meta account saved",
      failure: "Could not save the Meta account",
      invalidate: [marketingKeys.connection],
    }),
    removeConnection: useAction(deleteMarketingConnection, {
      success: "Meta account removed",
      failure: "Could not remove the Meta account",
      invalidate: [marketingKeys.connection, ...CAMPAIGN_VIEWS, ["marketing", "posts"]],
    }),
    testConnection: useAction(testMarketingConnection, {
      failure: "Could not test the connection",
      invalidate: [marketingKeys.connection],
    }),
    sync: useAction(syncCampaigns, {
      failure: "Could not sync campaigns from Meta",
      invalidate: CAMPAIGN_VIEWS,
    }),
    refresh: useAction(refreshCampaignInsights, {
      success: "Results refreshed",
      failure: "Could not refresh results",
      invalidate: CAMPAIGN_VIEWS,
    }),
    start: useAction(activateCampaign, {
      success: "Campaign started on Meta",
      failure: "Meta could not start the campaign",
      invalidate: [...CAMPAIGN_VIEWS, ["marketing", "posts"]],
    }),
    pause: useAction(pauseCampaign, {
      success: "Campaign paused on Meta",
      failure: "Meta could not pause the campaign",
      invalidate: [...CAMPAIGN_VIEWS, ["marketing", "posts"]],
    }),
    createCampaign: useAction(createPausedCampaign, {
      success: "Paused campaign created",
      failure: "Meta could not create the campaign",
      invalidate: CAMPAIGN_VIEWS,
    }),
    boost: useAction(boostPost, {
      success: "Boost created paused. Start it from Campaigns when you are ready.",
      failure: "Meta could not create the boost",
      invalidate: [...CAMPAIGN_VIEWS, ["marketing", "posts"]],
    }),
    uploadImage: useAction(uploadAdImage, {
      failure: "Meta could not accept the image",
      invalidate: [],
    }),
    createAd: useAction(createAd, {
      success: "Ad created paused. Start it from Campaigns when you are ready.",
      failure: "Meta could not create the ad",
      invalidate: CAMPAIGN_VIEWS,
    }),
  };
}
